import fetch from 'isomorphic-fetch';
import { AccessToken, mapAccessToken } from '~/Api/Authentication';

import ApiClientError from './ApiClientError';
import ApiClientErrorResponse from './ApiClientErrorResponse';
import ApiClientRequest from './ApiClientRequest';
import BrowserAccessTokenStorage from './BrowserAccessTokenStorage';

export interface Logger {
	logRequest(request: ApiClientRequest): void;
	logResponse(request: ApiClientRequest, response: Response): void;
	logError(request: ApiClientRequest, errorResponse: ApiClientErrorResponse): void;
}

export type HttpVerb = 'DELETE' | 'PUT' | 'GET' | 'POST';

export interface AccessTokenRequestResult {
	accessToken: AccessToken;
}

export default class ApiClient {
	baseUrl: string;

	tokenAccessStorage: BrowserAccessTokenStorage;

	logger: Logger;

	defaultHeaders: { [header: string]: string };

	constructor(
		baseUrl: string,
		tokenAccessStorage: BrowserAccessTokenStorage,
		logger: Logger,
		defaultHeaders: { [header: string]: string } = {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		}
	) {
		this.baseUrl = baseUrl;
		this.tokenAccessStorage = tokenAccessStorage;
		this.logger = logger;
		this.defaultHeaders = defaultHeaders;
	}

	async fetch(
		url: string,
		options: {
			body?: string;
			headers?: { [header: string]: string };
			method?: HttpVerb;
		} = {}
	): Promise<Response> {
		const token = this.loadAccessToken();
		const headers = options.headers || {};

		if (!token) {
			throw this.createMissingAccessTokenResponse();
		}

		headers.Authorization = `Bearer ${token}`;

		try {
			return await this.unauthenticatedFetch(url, { ...options, headers });
		} catch (errorResponse) {
			if ((errorResponse as ApiClientErrorResponse).statusCode === 401) {
				this.tokenAccessStorage.removeToken();
			}
			throw errorResponse;
		}
	}

	async unauthenticatedFetch(
		url: string,
		options: {
			body?: string;
			headers?: { [header: string]: string };
			method?: HttpVerb;
		} = {}
	): Promise<Response> {
		const headers = {
			...this.defaultHeaders,
			...('headers' in options ? options.headers : {}),
		};

		Object.keys(headers).forEach((header) => {
			if (headers[header] === undefined) {
				delete headers[header];
			}
		});

		const request = new ApiClientRequest(this.baseUrl + url, {
			...options,
			headers,
		});
		this.logger.logRequest(request);

		try {
			const response = await this.processResponse(request, await fetch(request.url, request.options));
			this.logger.logResponse(request, response);
			return response;
		} catch (errorResponse) {
			const error =
				errorResponse instanceof ApiClientErrorResponse
					? errorResponse
					: this.createErrorResponse(request, null, [
							new ApiClientError('network-error', (errorResponse as { message?: string }).message || ''),
					  ]);
			this.logger.logError(request, error);
			throw error;
		}
	}

	async processResponse(request: ApiClientRequest, response: Response): Promise<Response> {
		if (response.status === 200 || response.status === 201) {
			return response;
		}

		const acceptContentType = request.options.headers.Accept;
		const contentType = response.headers.get('Content-Type');
		if (
			!contentType ||
			contentType.length < acceptContentType.length ||
			contentType.slice(0, acceptContentType.length) !== acceptContentType
		) {
			throw this.createErrorResponse(request, response.status, [
				new ApiClientError('unexpected-content-type', 'Unexpected content type'),
			]);
		}

		const responseData = await response.json();
		const errors = responseData.errors.map(
			(error: ApiClientError) => new ApiClientError(error.code, error.title, error.details, error.source, error.meta)
		);
		throw this.createErrorResponse(request, response.status, errors);
	}

	createErrorResponse(
		request: ApiClientRequest | null,
		statusCode: number | null,
		errors: ApiClientError[]
	): ApiClientErrorResponse {
		return new ApiClientErrorResponse(request, statusCode, errors);
	}

	createMissingAccessTokenResponse(): ApiClientErrorResponse {
		return this.createErrorResponse(null, null, [
			new ApiClientError('missing-access-token', 'Access token is missing'),
		]);
	}

	saveAccessToken(token: string) {
		this.tokenAccessStorage.saveToken(token);
	}

	loadAccessToken(): string | null {
		return this.tokenAccessStorage.loadToken();
	}

	fetchAccessToken(): Promise<AccessTokenRequestResult> {
		return this.fetch('authentication', {})
			.then((response) => response.json())
			.then((response) => ({
				accessToken: mapAccessToken(response.accessToken),
			}));
	}

	removeAccessToken() {
		this.tokenAccessStorage.removeToken();
	}
}
